import * as React from "react";

import { RefObjectFunction, useMergedRefs } from "@fluentui/react-hooks";
import { IKeytipProps, useKeytipRef } from "@fluentui/react/lib/Keytips";

import { InputMethod } from "@1js/acui-common";
import { defaultKeytipExecute } from "@1js/acui-keytip";

import { deriveButtonKeytip } from "../../../utilities";
import { AppButtonProps } from "./AppButton.types";

/**
 * Returns the given ref merged with a local ref controlling a keytip.
 * Once the returned ref is attached to your button component, the button will have a keytip.
 */
export const useAppButtonKeytip = (
  ref: RefObjectFunction<HTMLElement | null | undefined>,
  props: AppButtonProps,
  opts?: { keytipProps?: IKeytipProps; useDirectClick?: boolean }
) => {
  const { onExecute, id, location, shouldTakeFocus, toggled = false } = props;
  const { useDirectClick, keytipProps } = opts || {};

  // Execution could mean either directly clicking the control, or (where possible) only dispatching onExecute command
  const executeFromKeytip = React.useCallback(() => {
    if (useDirectClick) {
      ref?.current?.click();
    } else if (onExecute) {
      onExecute({
        id,
        location,
        toggled,
        inputMethod: InputMethod.Keyboard,
        shouldTakeFocus,
      });
    }
  }, [useDirectClick, ref, onExecute, id, location, shouldTakeFocus, toggled]);

  const keytipExecute = React.useCallback(
    (executeTarget: HTMLElement) => {
      defaultKeytipExecute(executeTarget, executeFromKeytip);
    },
    [executeFromKeytip]
  );
  const keytipRefData = React.useMemo(() => {
    return { keytipProps: deriveButtonKeytip(keytipProps, keytipExecute) };
  }, [keytipProps, keytipExecute]);

  const localRef = useKeytipRef(keytipRefData);
  const mergedRef = useMergedRefs<HTMLElement | null | undefined>(
    ref,
    localRef
  );
  return mergedRef;
};
